import { useState, useCallback, useRef, useEffect } from 'react';
import { generateUGCVideo, pollUGCVideoStatus } from '../../../services/contentService';
import { Avatar, Voice, GenerateUGCVideoParams, UGCAvatarLayout, UGCVideoFormat, HeyGenEngine, HeyGenExpressiveness, UGCAspectRatio, UGCVideoResolution } from '../types';
import { normalizeUGCVideoError } from '../utils/ugcVideoErrorMessages';
import { cleanVoiceoverScript } from '../utils/cleanVoiceoverScript';
import { debug } from "../../../utils/debugLogger";
import { getFreshIdToken } from "../../../auth/getFreshIdToken";

interface UseUGCVideoGenerationProps {
  customerId: string | null;
  idToken: string;
  selectedAvatar: Avatar | null;
  selectedVoice: Voice | null;
}

interface UseUGCVideoGenerationReturn {
  isGenerating: boolean;
  generatedVideoUrl: string | null;
  videoError: string | null;
  generationMessage: string | null;
  /** Backend status as returned (e.g. Queued, Audio, Video, Compositing, AVATAR_READY, FAILED; null when not generating) */
  videoStatus: string | null;
  /** Elapsed seconds since generation started (for wait time display) */
  elapsedSeconds: number;
  /** Base video duration in seconds from poll response when AVATAR_READY; used for timeline length on custom images step. */
  baseVideoDurationSeconds: number | null;
  /** Video ID from generate response; used for assembly (custom images) call. */
  generatedVideoId: string | null;
  /** Update the preview URL (e.g. after assembly/Creatomate polling returns final video). */
  updateVideoUrl: (url: string) => void;
  /**
   * Restore a previously generated video (e.g. when loading a saved draft). Applies the saved
   * id/url/duration/status immediately so the editor shows the video, then re-polls the backend
   * by video_id to refresh a possibly-expired URL.
   */
  restoreVideo: (record: {
    videoId: string | null;
    videoUrl: string | null;
    durationSeconds: number | null;
    status: string | null;
  }) => void;
  generateVideo: (
    script: string,
    options?: {
      video_format?: UGCVideoFormat;
      avatar_layout?: UGCAvatarLayout;
      engine?: HeyGenEngine;
      expressiveness?: HeyGenExpressiveness;
      motion_prompt?: string;
      aspect_ratio?: UGCAspectRatio;
      resolution?: UGCVideoResolution;
    }
  ) => Promise<void>;
  /** Resume polling after a soft timeout (e.g. user clicks "Check again"). No-op if no pending video. */
  retryCheck: () => void;
  reset: () => void;
}

/** Returns ms to wait before the next poll based on how many polls have run so far. */
function getPollInterval(attempt: number): number {
  if (attempt < 3) return 3_000;    // 0–2: every 3s (first 9s — fast initial checks)
  if (attempt < 8) return 5_000;    // 3–7: every 5s (next 25s)
  if (attempt < 20) return 10_000;  // 8–19: every 10s (next 120s)
  if (attempt < 40) return 15_000;  // 20–39: every 15s (next 300s)
  return 20_000;                    // 40+: every 20s (long tail)
}

const FIRST_POLL_DELAY_MS = 2_000;   // start checking 2s after the API call returns (faster initial check)
// ~32 min of active polling before the soft timeout. The backend bounds a HeyGen job at its
// 1800s (30 min) task heartbeat plus GenerateAudio ahead of it, then fails it via
// MarkPipelineFailed — so the frontend must keep polling at least that long or it stops
// showing progress (and the user re-generates, burning a second billable render) while the
// backend is still working. Curve: 9s + 25s + 120s + 300s then 20s × 75 ≈ 1954s.
const MAX_POLL_ATTEMPTS = 115;
const INITIAL_API_TIMEOUT_MS = 30_000; // 30 second timeout for initial generate API call
const MAX_ELAPSED_DISPLAY_SECONDS = 2_400; // 40 min — keep the wait-time counter moving past 30 min

/** Pending generation persistence — survives page refresh and chat re-entry. */
const PENDING_STORAGE_PREFIX = 'ugc:pending-video:';
const PENDING_TTL_MS = 40 * 60 * 1000; // 40 minutes — longer than the backend's 30 min HeyGen cap + finalize

interface PendingVideoRecord {
  video_id: string;
  started_at: number;
}

function pendingStorageKey(customerId: string): string {
  return PENDING_STORAGE_PREFIX + customerId;
}

function savePendingVideo(customerId: string | null, videoId: string): void {
  if (!customerId || typeof window === 'undefined') return;
  try {
    const record: PendingVideoRecord = { video_id: videoId, started_at: Date.now() };
    window.localStorage.setItem(pendingStorageKey(customerId), JSON.stringify(record));
  } catch {
    // localStorage disabled or full — drop silently; the in-memory ref still works for this session.
  }
}

function clearPendingVideo(customerId: string | null): void {
  if (!customerId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(pendingStorageKey(customerId));
  } catch { /* ignore */ }
}

function readPendingVideo(customerId: string): PendingVideoRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(pendingStorageKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingVideoRecord>;
    if (!parsed?.video_id || typeof parsed.started_at !== 'number') {
      window.localStorage.removeItem(pendingStorageKey(customerId));
      return null;
    }
    if (Date.now() - parsed.started_at > PENDING_TTL_MS) {
      window.localStorage.removeItem(pendingStorageKey(customerId));
      return null;
    }
    return { video_id: parsed.video_id, started_at: parsed.started_at };
  } catch {
    return null;
  }
}

export const useUGCVideoGeneration = ({
  customerId,
  idToken,
  selectedAvatar,
  selectedVoice,
}: UseUGCVideoGenerationProps): UseUGCVideoGenerationReturn => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [generationMessage, setGenerationMessage] = useState<string | null>(null);
  const [videoStatus, setVideoStatus] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [baseVideoDurationSeconds, setBaseVideoDurationSeconds] = useState<number | null>(null);
  const [generatedVideoId, setGeneratedVideoId] = useState<string | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef(0);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);
  /** video_id preserved after a soft timeout so user can resume polling via retryCheck. */
  const pendingVideoIdRef = useRef<string | null>(null);
  /**
   * Latest idToken kept in a ref so in-flight polling chains (HeyGen can take 20 min) always
   * read the freshest token after useTokenRefresh rotates it, instead of using the stale token
   * captured in the original useCallback closure — which would 401 and boot the user.
   */
  const idTokenRef = useRef(idToken);
  useEffect(() => {
    idTokenRef.current = idToken;
  }, [idToken]);

  const generateVideo = useCallback(
    async (
      script: string,
      options?: {
        video_format?: UGCVideoFormat;
        avatar_layout?: UGCAvatarLayout;
        engine?: HeyGenEngine;
        expressiveness?: HeyGenExpressiveness;
        motion_prompt?: string;
        aspect_ratio?: UGCAspectRatio;
        resolution?: UGCVideoResolution;
      }
    ) => {
      // Validate all required params exist
      if (!customerId || !idToken || !selectedAvatar || !selectedVoice || !script.trim()) {
        setVideoError('Missing required information for video generation');
        return;
      }

      const textForApi = cleanVoiceoverScript(script);
      if (!textForApi.trim()) {
        setVideoError('Script is empty after removing headings and labels. Add spoken lines or use Clean for voiceover, then try again.');
        return;
      }

      setIsGenerating(true);
      setVideoError(null);
      setGeneratedVideoUrl(null);
      setGenerationMessage(null);
      setVideoStatus(null);
      setElapsedSeconds(0);
      setBaseVideoDurationSeconds(null);
      setGeneratedVideoId(null);
      pollingAttemptsRef.current = 0;

      try {
        // Custom avatars use talking_photo_id; pre-made avatars use avatar_id (HeyGen)
        const isTalkingPhoto = selectedAvatar.avatar_type === 'talking_photo';
        const videoFormat = options?.video_format ?? 'reel';
        const params: GenerateUGCVideoParams = {
          customer_id: customerId,
          voice_id: selectedVoice.voice_id,
          // Custom (cloned) voices are ElevenLabs — the pipeline runs TTS + HeyGen audio mode.
          ...(selectedVoice.provider === 'ELEVENLABS' || selectedVoice.is_custom
            ? { voice_provider: 'ELEVENLABS' }
            : {}),
          text: textForApi,
          video_format: videoFormat,
          aspect_ratio: options?.aspect_ratio,
          resolution: options?.resolution ?? '1080p',
        };
        if (options?.avatar_layout && Object.keys(options.avatar_layout).length > 0) {
          params.avatar_layout = options.avatar_layout;
        }
        if (options?.engine) {
          params.engine = { type: options.engine };
        }
        // expressiveness and motion_prompt are Avatar IV only — Avatar V rejects them
        const isAvatarIV = !options?.engine || options.engine === 'avatar_iv';
        if (isAvatarIV) {
          if (options?.expressiveness) params.expressiveness = options.expressiveness;
          const trimmedPrompt = options?.motion_prompt?.trim();
          if (trimmedPrompt) params.motion_prompt = trimmedPrompt;
        }
        if (isTalkingPhoto) {
          params.talking_photo_id = selectedAvatar.avatar_id;
        } else {
          params.avatar_id = selectedAvatar.avatar_id;
        }

        // Add timeout to initial API call to prevent infinite hanging
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Video generation request timed out. Please check your connection and try again.')), INITIAL_API_TIMEOUT_MS)
        );

        const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

        debug.log('🎬 [HeyGen] Calling generateUGCVideo API...');
        debug.log('📝 [HeyGen] Request params:', params);
        const tokenForGenerate = await getFreshIdToken(idTokenRef.current);
        idTokenRef.current = tokenForGenerate;
        const response = await Promise.race([generateUGCVideo(params, tokenForGenerate, idempotencyKey), timeoutPromise as Promise<any>]);
        debug.log('✅ [HeyGen] API Response received:', response);
        debug.log('📊 [HeyGen] Full response object:', JSON.stringify(response, null, 2));

        // Log HeyGen-specific information
        if (response.status) {
          debug.log(`🔄 [HeyGen] Status: ${response.status}`);
        }
        if (response.error) {
          debug.log(`❌ [HeyGen] Error: ${response.error}`);
          debug.log(`⚠️  [HeyGen] This error indicates:`, response.error);
        }
        if (response.argil_video_id) {
          debug.log(`🎥 [HeyGen] Argil Video ID: ${response.argil_video_id}`);
        }

        // Store the generation message from the response
        if (response.message) {
          setGenerationMessage(response.message);
          debug.log('💬 [HeyGen] Generation message:', response.message);
        }

        if (response.video_id) {
          debug.log(`✨ [HeyGen] Video ID received: ${response.video_id}`);
          setGeneratedVideoId(response.video_id);
          pendingVideoIdRef.current = response.video_id;
          savePendingVideo(customerId, response.video_id);
          if (response.status) {
            debug.log(`📍 [HeyGen] Initial video status: ${response.status}`);
            if (response.status === 'FAILED') {
              debug.log(`❌ [HeyGen] VIDEO GENERATION FAILED!`, response);
              debug.log(`💳 [HeyGen] Check: Is the error "Insufficient credit"?`, response.error);
            }
            setVideoStatus(response.status);
          }

          const startPolling = (videoId: string) => {
            pollingAttemptsRef.current = 0;

            const pollStatus = async () => {
              try {
                const attempt = pollingAttemptsRef.current;
                debug.log(`🔄 [HeyGen] Polling video status (attempt ${attempt + 1}/60)...`);
                const pollToken = await getFreshIdToken(idTokenRef.current);
                idTokenRef.current = pollToken;
                const statusResponse = await pollUGCVideoStatus(videoId, pollToken);
                debug.log(`📊 [HeyGen] Poll response: status=${statusResponse.status}`, statusResponse);
                const videoUrl = statusResponse.avatar_video_url ?? statusResponse.cloudfront_url;

                if (statusResponse.status === 'AVATAR_READY' && videoUrl) {
                  debug.log('✅ [HeyGen] VIDEO READY! URL:', videoUrl);
                  debug.log('⏱️  [HeyGen] Generation completed after attempt', attempt + 1);
                  if (elapsedIntervalRef.current) {
                    clearInterval(elapsedIntervalRef.current);
                    elapsedIntervalRef.current = null;
                  }
                  pendingVideoIdRef.current = null;
                  clearPendingVideo(customerId);
                  setGeneratedVideoUrl(videoUrl);
                  setVideoStatus('AVATAR_READY');
                  if (typeof statusResponse.duration_seconds === 'number' && statusResponse.duration_seconds > 0) {
                    setBaseVideoDurationSeconds(statusResponse.duration_seconds);
                  }
                  setIsGenerating(false);
                  pollingAttemptsRef.current = 0;
                  return;
                }

                if (statusResponse.status === 'FAILED') {
                  debug.log('❌ [HeyGen] VIDEO GENERATION FAILED!');
                  debug.log('📋 [HeyGen] Error from HeyGen:', statusResponse.error);
                  if (statusResponse.error?.includes('credit') || statusResponse.error?.includes('Credit')) {
                    debug.log('💳 [HeyGen] CONFIRMED: Insufficient API Credits detected');
                    debug.log('💳 [HeyGen] Action required: Add API credits to HeyGen account at https://www.heygen.com/settings/billing');
                  }
                  if (elapsedIntervalRef.current) {
                    clearInterval(elapsedIntervalRef.current);
                    elapsedIntervalRef.current = null;
                  }
                  pendingVideoIdRef.current = null;
                  clearPendingVideo(customerId);
                  setVideoError(normalizeUGCVideoError(statusResponse.error || 'Failed to generate video'));
                  setVideoStatus('FAILED');
                  setIsGenerating(false);
                  pollingAttemptsRef.current = 0;
                  return;
                }

                if (pollingAttemptsRef.current < MAX_POLL_ATTEMPTS) {
                  setVideoStatus(statusResponse.status);
                  const delay = getPollInterval(pollingAttemptsRef.current);
                  pollingAttemptsRef.current++;
                  pollingTimeoutRef.current = setTimeout(pollStatus, delay);
                } else {
                  // Soft timeout: stop active polling but keep video ID so user can retry
                  if (elapsedIntervalRef.current) {
                    clearInterval(elapsedIntervalRef.current);
                    elapsedIntervalRef.current = null;
                  }
                  setVideoError(
                    'Still processing — HeyGen videos can take up to 30 minutes. Click "Check again" to resume checking.'
                  );
                  setVideoStatus('PENDING');
                  setIsGenerating(false);
                  pollingAttemptsRef.current = 0;
                }
              } catch (err) {
                debug.error('Error polling video status:', err);
                if (pollingAttemptsRef.current < MAX_POLL_ATTEMPTS) {
                  const delay = getPollInterval(pollingAttemptsRef.current);
                  pollingAttemptsRef.current++;
                  pollingTimeoutRef.current = setTimeout(pollStatus, delay);
                } else {
                  if (elapsedIntervalRef.current) {
                    clearInterval(elapsedIntervalRef.current);
                    elapsedIntervalRef.current = null;
                  }
                  setVideoError(normalizeUGCVideoError(err));
                  setVideoStatus(null);
                  setIsGenerating(false);
                  pollingAttemptsRef.current = 0;
                }
              }
            };

            pollingTimeoutRef.current = setTimeout(pollStatus, FIRST_POLL_DELAY_MS);
          };

          // Start elapsed time counter
          elapsedIntervalRef.current = setInterval(() => {
            setElapsedSeconds((s) => Math.min(s + 1, MAX_ELAPSED_DISPLAY_SECONDS));
          }, 1000);

          startPolling(response.video_id);
        } else {
          const errorMsg = 'Failed to start video generation. No video ID returned.';
          debug.log(`❌ [HeyGen] ${errorMsg}`);
          debug.log('📋 [HeyGen] Response received:', response);
          setVideoError(normalizeUGCVideoError(errorMsg));
          setVideoStatus(null);
          setIsGenerating(false);
          setElapsedSeconds(0);
        }
      } catch (err: any) {
        debug.error('❌ [HeyGen] Error generating video:', err);
        debug.log('🔍 [HeyGen] Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          isNetworkError: err.code === 'ERR_NETWORK',
        });
        if (err.response?.status === 402) {
          setVideoError('INSUFFICIENT_CREDITS');
        } else {
          if (err.response?.data?.error?.includes('credit')) {
            debug.log('💳 [HeyGen] CREDIT ERROR DETECTED - HeyGen account may have insufficient API credits');
          }
          setVideoError(normalizeUGCVideoError(err));
        }
        setVideoStatus(null);
        setIsGenerating(false);
        setElapsedSeconds(0);
      }
    },
    [customerId, idToken, selectedAvatar, selectedVoice]
  );

  const updateVideoUrl = useCallback((url: string) => {
    setGeneratedVideoUrl(url);
  }, []);

  const restoreVideo = useCallback((record: {
    videoId: string | null;
    videoUrl: string | null;
    durationSeconds: number | null;
    status: string | null;
  }) => {
    // Stop any in-flight polling/elapsed timers and clear pending generation state.
    if (pollingTimeoutRef.current) { clearTimeout(pollingTimeoutRef.current); pollingTimeoutRef.current = null; }
    if (elapsedIntervalRef.current) { clearInterval(elapsedIntervalRef.current); elapsedIntervalRef.current = null; }
    pollingAttemptsRef.current = 0;
    pendingVideoIdRef.current = null;
    setVideoError(null);
    setGenerationMessage(null);
    setElapsedSeconds(0);
    setIsGenerating(false);

    // Apply the saved values immediately so the editor renders the video right away.
    setGeneratedVideoId(record.videoId);
    setGeneratedVideoUrl(record.videoUrl);
    setBaseVideoDurationSeconds(record.durationSeconds);
    setVideoStatus(record.status);

    // Best-effort refresh from the backend status record: the saved avatar URL may have expired,
    // and this fills in anything the draft was missing. Non-blocking — failures keep saved values.
    if (record.videoId) {
      (async () => {
        try {
          const token = await getFreshIdToken(idTokenRef.current);
          idTokenRef.current = token;
          const status = await pollUGCVideoStatus(record.videoId as string, token);
          const freshUrl = status.cloudfront_url ?? status.avatar_video_url;
          if (freshUrl) setGeneratedVideoUrl(freshUrl);
          if (typeof status.duration_seconds === 'number' && status.duration_seconds > 0) {
            setBaseVideoDurationSeconds(status.duration_seconds);
          }
          if (status.status) setVideoStatus(status.status);
        } catch {
          // Keep the saved values; the draft is still usable from what was persisted.
        }
      })();
    }
  }, []);

  const retryCheck = useCallback(() => {
    const videoId = pendingVideoIdRef.current;
    if (!videoId || isGenerating) return;

    setVideoError(null);
    setIsGenerating(true);
    pollingAttemptsRef.current = 0;

    const pollStatus = async () => {
      try {
        const pollToken = await getFreshIdToken(idTokenRef.current);
        idTokenRef.current = pollToken;
        const statusResponse = await pollUGCVideoStatus(videoId, pollToken);
        const videoUrl = statusResponse.avatar_video_url ?? statusResponse.cloudfront_url;

        if (statusResponse.status === 'AVATAR_READY' && videoUrl) {
          pendingVideoIdRef.current = null;
          clearPendingVideo(customerId);
          setGeneratedVideoUrl(videoUrl);
          setVideoStatus('AVATAR_READY');
          if (typeof statusResponse.duration_seconds === 'number' && statusResponse.duration_seconds > 0) {
            setBaseVideoDurationSeconds(statusResponse.duration_seconds);
          }
          setIsGenerating(false);
          pollingAttemptsRef.current = 0;
          return;
        }
        if (statusResponse.status === 'FAILED') {
          pendingVideoIdRef.current = null;
          clearPendingVideo(customerId);
          setVideoError(normalizeUGCVideoError(statusResponse.error || 'Failed to generate video'));
          setVideoStatus('FAILED');
          setIsGenerating(false);
          pollingAttemptsRef.current = 0;
          return;
        }
        if (pollingAttemptsRef.current < MAX_POLL_ATTEMPTS) {
          setVideoStatus(statusResponse.status);
          const delay = getPollInterval(pollingAttemptsRef.current);
          pollingAttemptsRef.current++;
          pollingTimeoutRef.current = setTimeout(pollStatus, delay);
        } else {
          setVideoError('Still processing — HeyGen videos can take up to 30 minutes. Click "Check again" to resume checking.');
          setVideoStatus('PENDING');
          setIsGenerating(false);
          pollingAttemptsRef.current = 0;
        }
      } catch (err) {
        debug.error('Error polling video status:', err);
        if (pollingAttemptsRef.current < MAX_POLL_ATTEMPTS) {
          const delay = getPollInterval(pollingAttemptsRef.current);
          pollingAttemptsRef.current++;
          pollingTimeoutRef.current = setTimeout(pollStatus, delay);
        } else {
          setVideoError(normalizeUGCVideoError(err));
          setVideoStatus(null);
          setIsGenerating(false);
          pollingAttemptsRef.current = 0;
        }
      }
    };

    pollingTimeoutRef.current = setTimeout(pollStatus, FIRST_POLL_DELAY_MS);
  }, [customerId, idToken, isGenerating]);

  const reset = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    pollingAttemptsRef.current = 0;
    pendingVideoIdRef.current = null;
    clearPendingVideo(customerId);
    setGeneratedVideoUrl(null);
    setVideoError(null);
    setGenerationMessage(null);
    setVideoStatus(null);
    setElapsedSeconds(0);
    setBaseVideoDurationSeconds(null);
    setGeneratedVideoId(null);
    setIsGenerating(false);
  }, [customerId]);

  // Resume an in-flight generation from a previous session/page if localStorage has
  // a pending video_id for this customer. One-shot: only runs the first time we have
  // a customer + token (covers initial mount and async auth hydration).
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    if (!customerId || !idToken) return;
    const pending = readPendingVideo(customerId);
    if (!pending) return;

    resumedRef.current = true;
    pendingVideoIdRef.current = pending.video_id;
    setGeneratedVideoId(pending.video_id);
    setVideoStatus('Resuming…');

    // Backfill elapsed counter from started_at so the user sees realistic wait time.
    const startedSecondsAgo = Math.floor((Date.now() - pending.started_at) / 1000);
    setElapsedSeconds(Math.min(Math.max(startedSecondsAgo, 0), MAX_ELAPSED_DISPLAY_SECONDS));
    elapsedIntervalRef.current = setInterval(() => {
      setElapsedSeconds((s) => Math.min(s + 1, MAX_ELAPSED_DISPLAY_SECONDS));
    }, 1000);

    // Kick off polling via the existing retryCheck path; safe because isGenerating is
    // currently false and pendingVideoIdRef is set.
    retryCheck();
    // retryCheck is intentionally omitted from deps so this only fires once when
    // credentials first land; including it would re-fire whenever isGenerating flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, idToken]);

  // Cleanup polling and elapsed timer on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
      }
    };
  }, []);

  return {
    isGenerating,
    generatedVideoUrl,
    videoError,
    generationMessage,
    videoStatus,
    elapsedSeconds,
    baseVideoDurationSeconds,
    generatedVideoId,
    updateVideoUrl,
    restoreVideo,
    generateVideo,
    retryCheck,
    reset,
  };
};

export default useUGCVideoGeneration;
